import { useEffect, useState } from "react";
import axios from "axios";
import { Trash, ArrowCounterClockwise, Warning, FileText, Receipt, Truck, Note } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Icon per entity_type so users can spot the doc kind at a glance.
const ENTITY_ICON = {
  invoice: Receipt,
  debit_note: Note,
  credit_note: Note,
  letterhead: FileText,
  transport_invoice: Truck,
};

// Muted colour swatch per entity_type — soft badge next to each row.
const ENTITY_TINT = {
  invoice: "bg-blue-100 text-blue-700",
  debit_note: "bg-amber-100 text-amber-700",
  credit_note: "bg-emerald-100 text-emerald-700",
  letterhead: "bg-violet-100 text-violet-700",
  transport_invoice: "bg-sky-100 text-sky-700",
};

/**
 * Recycle Bin — soft-deleted documents live here until the user either
 * restores them back to their original module or nukes them permanently.
 *
 * Access is scoped to admin / super_admin roles at the backend; the
 * sidebar link is likewise hidden for viewer/staff so this page is only
 * discoverable to the people who can actually act on it.
 */
export default function RecycleBin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [working, setWorking] = useState(null); // id currently being acted upon

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/recycle-bin`);
      setRows(r.data || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load recycle bin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleRestore = async (id, label, title) => {
    if (!window.confirm(`Restore ${label} ${title} back to its original section?`)) return;
    setWorking(id);
    try {
      await axios.post(`${API}/recycle-bin/${id}/restore`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Restore failed");
    } finally {
      setWorking(null);
    }
  };

  const handlePermanent = async (id, label, title) => {
    if (!window.confirm(`PERMANENTLY delete ${label} ${title}? This cannot be undone.`)) return;
    setWorking(id);
    try {
      await axios.delete(`${API}/recycle-bin/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    } finally {
      setWorking(null);
    }
  };

  const handleEmpty = async () => {
    if (rows.length === 0) return;
    if (!window.confirm(`Permanently delete ALL ${rows.length} items in the Recycle Bin? This cannot be undone.`)) return;
    setWorking("__all__");
    try {
      await axios.delete(`${API}/recycle-bin`);
      setRows([]);
    } catch (e) {
      alert(e?.response?.data?.detail || "Empty bin failed");
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="recycle-bin-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2 text-slate-500 text-xs font-bold uppercase tracking-widest">
            <Trash size={14} weight="fill" /> Deleted items live here
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Recycle Bin</h1>
          <p className="text-sm text-slate-500 mt-1">
            Anything you delete — invoices, notes, letterheads, transport bills — waits here.
            Restore returns it to its original section. Permanent delete cannot be undone.
          </p>
        </div>
        {rows.length > 0 && (
          <button
            onClick={handleEmpty}
            disabled={working === "__all__"}
            data-testid="empty-bin-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm font-bold tracking-wide"
          >
            <Trash size={16} weight="fill" /> Empty Bin ({rows.length})
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-sm bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center gap-2">
          <Warning size={18} /> {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center">
            <Trash size={40} weight="duotone" className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold mb-1">Recycle Bin is empty</p>
            <p className="text-slate-500 text-sm">
              Nothing to restore right now. Deleted invoices, notes and letterheads will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto sticky-table">
            <table className="min-w-full text-sm">
              <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Identifier</th>
                  <th className="px-4 py-3 text-left">Deleted By</th>
                  <th className="px-4 py-3 text-left">Deleted On</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const Icon = ENTITY_ICON[r.entity_type] || FileText;
                  const tint = ENTITY_TINT[r.entity_type] || "bg-slate-100 text-slate-600";
                  const busy = working === r.id;
                  const when = r.deleted_at ? new Date(r.deleted_at).toLocaleString() : "—";
                  return (
                    <tr key={r.id} data-testid={`bin-row-${r.id}`} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest ${tint}`}>
                          <Icon size={12} weight="fill" /> {r.entity_label}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-slate-900">{r.title}</td>
                      <td className="px-4 py-3 text-slate-700">{r.deleted_by_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{when}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleRestore(r.id, r.entity_label, r.title)}
                            disabled={busy}
                            data-testid={`bin-restore-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-emerald-700 hover:bg-emerald-50 border border-emerald-200 text-xs font-bold tracking-wide disabled:opacity-60"
                            title="Restore to original section"
                          >
                            <ArrowCounterClockwise size={14} weight="bold" /> Restore
                          </button>
                          <button
                            onClick={() => handlePermanent(r.id, r.entity_label, r.title)}
                            disabled={busy}
                            data-testid={`bin-delete-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-rose-700 hover:bg-rose-50 border border-rose-200 text-xs font-bold tracking-wide disabled:opacity-60"
                            title="Permanently delete — cannot be undone"
                          >
                            <Trash size={14} weight="fill" /> Delete Forever
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
